import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import toast, { Toaster as NotificationBase, ToastPositions } from '../toast';
import Icon, { IconColors, IconSizes, IconTypes } from '../icon';
import './notification.scss';
import useTranslation from '../../../utils/translation-utils';

const DEFAULT_DURATION = 4000;

const NotificationTypes = {
  SUCCESS: 'noti-success',
  WARNING: 'noti-warning',
  ERROR: 'noti-error',
  CUSTOM: 'noti-custom',
};

const Notification = ({
  thisToast, type, text, supportingText, customView, showClose,
}) => {
  const [getTranslation] = useTranslation();
  const bodyClassNames = clsx('noti-body', { 'noti-no-sup-text': !supportingText.trim() });

  const getIcon = () => {
    let iconType;
    let iconColor;

    switch (type) {
      case NotificationTypes.ERROR:
        iconType = IconTypes.ALERT_CIRCLE;
        iconColor = IconColors.ERROR;
        break;
      case NotificationTypes.WARNING:
        iconType = IconTypes.ALERT_TRIANGLE;
        iconColor = IconColors.WARNING;
        break;
      case NotificationTypes.SUCCESS:
        iconType = IconTypes.CHECK_CIRCLE;
        iconColor = IconColors.SUCCESS;
        break;
      default:
        return null;
    }

    return (
      <div className="noti-icon">
        <Icon type={iconType} color={iconColor} size={IconSizes.MEDIUM} />
      </div>
    );
  };

  const getBody = () => {
    switch (type) {
      case NotificationTypes.ERROR:
      case NotificationTypes.WARNING:
      case NotificationTypes.SUCCESS:
        return (
          <div className={bodyClassNames}>
            <div className="noti-text">{getTranslation(text)}</div>
            <div className="noti-supporting-text">{getTranslation(supportingText)}</div>
          </div>
        );
      case NotificationTypes.CUSTOM:
        return (
          <div className="noti-body">
            {customView}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`noti-cont ${type}`}>
      {getIcon()}
      {getBody()}
      {showClose ? (
        <Icon
          className="noti-close-icon"
          type={IconTypes.CROSS}
          size={IconSizes.MEDIUM}
          color={IconColors.GREY}
          onClick={() => toast.dismiss(thisToast.id)}
        />
      ) : null}
    </div>
  );
};

Notification.propTypes = {
  thisToast: PropTypes.shape({ id: PropTypes.string }),
  type: PropTypes.oneOf(Object.values(NotificationTypes)),
  text: PropTypes.string,
  supportingText: PropTypes.string,
  customView: PropTypes.element,
  showClose: PropTypes.bool,
};

Notification.defaultProps = {
  thisToast: {},
  type: NotificationTypes.SUCCESS,
  text: '',
  supportingText: '',
  customView: null,
  showClose: true,
};

const commonToastProperties = {
  duration: DEFAULT_DURATION,
  position: ToastPositions.TOP_RIGHT,
  className: 'noti-toast',
  ariaProps: {
    className: 'noti-toast-aria',
  },
};

const notification = {
  success: (text, supportingText) => toast((t) => (
    <Notification
      thisToast={t}
      text={text}
      supportingText={supportingText}
      type={NotificationTypes.SUCCESS}
    />
  ), {
    ...commonToastProperties,
  }),
  error: (text, supportingText) => toast((t) => (
    <Notification
      thisToast={t}
      text={text}
      supportingText={supportingText}
      type={NotificationTypes.ERROR}
    />
  ), {
    ...commonToastProperties,
    duration: 8000,
  }),
  warning: (text, supportingText) => toast((t) => (
    <Notification
      thisToast={t}
      text={text}
      supportingText={supportingText}
      type={NotificationTypes.WARNING}
    />
  ), {
    ...commonToastProperties,
  }),
  custom: (view, duration, showClose) => toast((t) => (
    <Notification
      thisToast={t}
      customView={view}
      type={NotificationTypes.CUSTOM}
      showClose={showClose}
    />
  ), {
    ...commonToastProperties,
    className: 'noti-toast noti-custom',
    duration: duration || DEFAULT_DURATION,
  }),
  dismiss: (id) => toast.dismiss(id),
  remove: (id) => toast.remove(id),
};

export {
  notification as default,
  NotificationTypes,
  NotificationBase,
};
